import { useCallback, useEffect, useReducer, useState } from 'react'
import { saveAs } from 'file-saver'
import printJS from 'print-js'
import { useAuth0 } from '@auth0/auth0-react'
import { AuthenticatedHttpRequest, Fetch, FetchState, HttpRequest, HttpResponse, Page } from './api-types'
import { useStateWithSessionStorage } from '../hooks/common-hooks'

export function useAccessToken() {
  const { getAccessTokenSilently } = useAuth0()
  return useCallback(async (): Promise<string> => {
    const token = await getAccessTokenSilently()
    if (!token) {
      throw new Error('Kunne ikke hente tilgangstoken')
    }
    return token
  }, [getAccessTokenSilently])
}

type ApiCallback<T> = (request: AuthenticatedHttpRequest) => Promise<HttpResponse<T>>
type FetchCallBack<T> = (request: HttpRequest) => Promise<HttpResponse<T>>
type PagableCallback<T> = (request: AuthenticatedHttpRequest) => Promise<HttpResponse<Page<T>>>

enum ActionState {
  FETCH_INIT,
  FETCH_SUCCESS,
  BLOB_FETCH_SUCCESS,
  FETCH_FAILURE,
  FETCH_SUCCESS_NO_CONTENT,
}

type Action<T> =
  | { type: ActionState.FETCH_INIT }
  | { type: ActionState.FETCH_SUCCESS; results: T }
  | {
      type: ActionState.BLOB_FETCH_SUCCESS
      blob: Blob
      fileName: string | undefined
    }
  | { type: ActionState.FETCH_FAILURE; error: unknown }
  | { type: ActionState.FETCH_SUCCESS_NO_CONTENT }

function createReducer<T>() {
  function dataFetchReducer(state: Fetch<T>, action: Action<T>): Fetch<T> {
    switch (action.type) {
      case ActionState.FETCH_INIT:
        return { status: FetchState.LOADING }
      case ActionState.FETCH_SUCCESS:
        return { status: FetchState.SUCCESS, data: action.results }
      case ActionState.FETCH_SUCCESS_NO_CONTENT:
        return { status: FetchState.SUCCESS_NO_CONTENT }
      case ActionState.BLOB_FETCH_SUCCESS:
        return {
          status: FetchState.BLOB_SUCCESS,
          blob: action.blob,
          fileName: action.fileName,
        }
      case ActionState.FETCH_FAILURE:
        return { status: FetchState.ERROR, error: action.error }
      default:
        return state
    }
  }
  return dataFetchReducer
}

type UseApiConfig<T> = {
  successCallback?: (data: T) => void
  errorCallback?: (error: string) => void
}
export function useApi<T>(
  apiCallback: ApiCallback<T>,
  config?: UseApiConfig<T>
): [Fetch<T>, (request: HttpRequest) => Promise<void>] {
  const [state, dispatch] = useReducer(createReducer<T>(), {
    status: FetchState.NOT_STARTED,
  })
  const getAccessToken = useAccessToken()

  const fetchData = async (request: HttpRequest) => {
    try {
      dispatch({ type: ActionState.FETCH_INIT })
      const token = await getAccessToken()
      const result = await apiCallback({ ...request, token })
      if (result.contentType === 'json' && result.json) {
        dispatch({ type: ActionState.FETCH_SUCCESS, results: result.json })
        if (config?.successCallback) {
          config.successCallback(result.json)
        }
      }
      if (result.contentType === 'blob' && result.blob) {
        dispatch({
          type: ActionState.BLOB_FETCH_SUCCESS,
          blob: result.blob,
          fileName: result.fileName,
        })
      }
      if (result.contentType === 'no-content') {
        dispatch({ type: ActionState.FETCH_SUCCESS_NO_CONTENT })
        if (config?.successCallback) {
          config.successCallback(result.nothing)
        }
      }
    } catch (error) {
      dispatch({ type: ActionState.FETCH_FAILURE, error })
      if (config?.errorCallback) {
        if (error instanceof Error) {
          config.errorCallback(error.message)
        } else {
          config.errorCallback(`Unexpected error occurred: ${error}`)
        }
      }
    }
  }

  return [state, fetchData]
}

export function useApiOnLoad<T>(
  apiCallback: ApiCallback<T>,
  initRequest: HttpRequest
): [Fetch<T>, (request: HttpRequest) => void] {
  const [request, setRequest] = useState<HttpRequest>(initRequest)
  const [response, doFetch] = useApi(apiCallback)
  const [isCalled, setCalled] = useState(true)

  useEffect(() => {
    const fetchData = async () => {
      doFetch(request)
    }
    if (isCalled) {
      fetchData()
      setCalled(false)
    }
  }, [doFetch, request, isCalled])

  const fetchData = (httpRequest: HttpRequest) => {
    setRequest(httpRequest)
    setCalled(true)
  }

  return [response, fetchData]
}

// TODO: Make this usable and use it for e.a dropdowns: ETU-7807
export function useCachableApi<T>(
  apiCallback: ApiCallback<T>,
  cacheName: string
): [Fetch<T>, (request: HttpRequest) => void] {
  const [response, setResponse] = useStateWithSessionStorage<Fetch<T>>(cacheName, {
    status: FetchState.NOT_STARTED,
  })
  const [state, setState] = useApi<T>(apiCallback)
  const [unmounted, setUnmounted] = useState(false)

  useEffect(() => {
    if (!unmounted && state.status === FetchState.SUCCESS) {
      setResponse(state)
    }
    return () => {
      setUnmounted(true)
    }
  }, [state, setResponse, unmounted])

  const fetchData = (httpRequest: HttpRequest) => {
    if (!unmounted && state.status === FetchState.NOT_STARTED && response.status === FetchState.NOT_STARTED) {
      setState(httpRequest)
    }
  }

  return [response.status === FetchState.SUCCESS ? response : state, fetchData]
}

export function useFetch<T>(callback: FetchCallBack<T>): [Fetch<T>, (request: HttpRequest) => Promise<void>] {
  const [state, dispatch] = useReducer(createReducer<T>(), {
    status: FetchState.NOT_STARTED,
  })

  const fetchData = async (request: HttpRequest) => {
    try {
      dispatch({ type: ActionState.FETCH_INIT })
      const result = await callback(request)

      if (result.contentType === 'json' && result.json) {
        dispatch({ type: ActionState.FETCH_SUCCESS, results: result.json })
      }
      if (result.contentType === 'text/html' && result.text) {
        dispatch({
          type: ActionState.FETCH_SUCCESS,
          results: result.text,
        })
      }
    } catch (error) {
      dispatch({ type: ActionState.FETCH_FAILURE, error })
    }
  }

  return [state, fetchData]
}

export function usePageableApi<T>(apiCallback: PagableCallback<T>, config?: UseApiConfig<Page<T>>) {
  return useApi<Page<T>>(apiCallback, config)
}

export function usePageableApiOnLoad<T>(apiCallback: PagableCallback<T>, initRequest: HttpRequest) {
  return useApiOnLoad<Page<T>>(apiCallback, initRequest)
}

export enum DownloadCompletedAction {
  SAVE,
  PRINT,
}

export type DownloadRequest = {
  id?: string
  query?: string
  body?: Record<string, unknown>
  backupFileName: string
}
export function useDownloadableApi(
  apiCallback: ApiCallback<undefined>,
  downloadCompletedAction: DownloadCompletedAction = DownloadCompletedAction.SAVE
): [Fetch<undefined>, (downloadRequest: DownloadRequest) => void] {
  const [hasDownloaded, setHasDownloaded] = useState(false)
  const [response, fetch] = useApi(apiCallback)
  const [request, setRequest] = useState()
  const [backupFileName, setBackUpFileName] = useState<string>('')

  if (response.status === FetchState.BLOB_SUCCESS && !hasDownloaded) {
    if (downloadCompletedAction === DownloadCompletedAction.PRINT) {
      printJS({ printable: URL.createObjectURL(response.blob), type: 'pdf' })
    } else {
      saveAs(response.blob, response.fileName ? response.fileName : backupFileName)
    }
    setHasDownloaded(true)
  }

  const download = (downloadRequest: DownloadRequest) => {
    if (
      (downloadRequest.query || downloadRequest.body || downloadRequest.id) &&
      JSON.stringify(downloadRequest) !== JSON.stringify(request)
    ) {
      fetch(downloadRequest)
      setRequest(request)
    }
    setHasDownloaded(false)
    setBackUpFileName(downloadRequest.backupFileName)
  }

  return [response, download]
}
